import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { OtmLogo } from "@/components/common/OtmLogo";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { getErrorMessage } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const expired = new URLSearchParams(window.location.search).get("expired") === "1";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      const msg = getErrorMessage(err, "Incorrect administrator password");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 grain relative">
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8">
          <OtmLogo imgClassName="h-11" className="items-center" />
        </div>
        <div className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#73bf44]/10 flex items-center justify-center">
              <ShieldCheck size={24} className="text-[#73bf44]" weight="duotone" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-white">Administrator Access</h1>
              <p className="text-xs text-neutral-500">OTM TECH AI Management Console</p>
            </div>
          </div>

          <form onSubmit={submit}>
            {expired && !error && (
              <div className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-4" data-testid="admin-session-expired">
                Your session ended or timed out. Please sign in again.
              </div>
            )}
            <label className="text-sm text-neutral-400 mb-2 block">Administrator Password</label>
            <div className="flex items-center gap-2 bg-black/40 border border-neutral-700 rounded-lg px-3 py-3 focus-within:border-[#73bf44] transition-colors mb-4">
              <Lock size={18} className="text-neutral-500" />
              <input
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoFocus
                className="bg-transparent outline-none text-white w-full placeholder:text-neutral-600"
              />
            </div>
            {error && <div className="text-sm text-red-400 mb-4" data-testid="admin-login-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full bg-[#73bf44] text-[#050505] rounded-full font-semibold py-3 hover:bg-[#5fa338] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
        <button
          onClick={() => navigate("/")}
          className="mt-6 w-full text-sm text-neutral-500 hover:text-white transition-colors"
          data-testid="admin-back-to-site"
        >
          ← Back to customer site
        </button>
      </div>
    </div>
  );
}
